import React, { useEffect, useState } from 'react';
import { Box, Typography, Paper, Table, TableHead, TableRow, TableCell, TableBody, Button, CircularProgress, InputAdornment, TextField, Dialog, DialogTitle, DialogContent, DialogActions, TableContainer } from '@mui/material';
import axios from 'axios';
import AdminCardEditDialog from '../components/AdminCardEditDialog';
import AdminCardIssueDialog from '../components/AdminCardIssueDialog';
import ConfirmDialog from '../components/ConfirmDialog';
import Toast from '../components/Toast';

export default function AdminCardsPage() {
  const [cards, setCards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editCard, setEditCard] = useState(null);
  const [editOpen, setEditOpen] = useState(false);
  const [issueOpen, setIssueOpen] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [toast, setToast] = useState({ open: false, message: '', severity: 'success' });
  const [editId, setEditId] = useState(null);
  const [search, setSearch] = useState('');
  const [applications, setApplications] = useState([]);
  const [appLoading, setAppLoading] = useState(true);
  const [appError, setAppError] = useState('');

  useEffect(() => {
    const token = localStorage.getItem('token');
    axios.get('https://api.safenetbanking.com/api/admin/cards', {
      headers: { Authorization: 'Bearer ' + token }
    }).then(res => {
      setCards(res.data);
      setLoading(false);
    });
    // Fetch card applications
    axios.get('https://api.safenetbanking.com/api/admin/card_applications', {
      headers: { Authorization: 'Bearer ' + token }
    })
      .then(res => {
        setApplications(res.data);
        setAppLoading(false);
      })
      .catch(() => {
        setAppError('Failed to load card applications');
        setAppLoading(false);
      });
  }, []);

  const handleEdit = card => {
    setEditCard(card);
    setEditId(card.id);
    setEditOpen(true);
  };
  const handleEditClose = () => setEditOpen(false);
  const handleEditSave = async updated => {
    const token = localStorage.getItem('token');
    try {
      await axios.patch(`https://api.safenetbanking.com/api/admin/cards/${updated.id}`, updated, {
        headers: { Authorization: 'Bearer ' + token }
      });
      setCards(cards.map(c => c.id === updated.id ? { ...c, ...updated } : c));
      setToast({ open: true, message: 'Card updated', severity: 'success' });
    } catch {
      setToast({ open: true, message: 'Update failed', severity: 'error' });
    }
    setEditOpen(false);
  };
  const handleIssue = () => setIssueOpen(true);
  const handleIssueClose = () => setIssueOpen(false);
  const handleIssueSave = async data => {
    const token = localStorage.getItem('token');
    try {
      await axios.post('https://api.safenetbanking.com/api/admin/cards', data, {
        headers: { Authorization: 'Bearer ' + token }
      });
      setCards([...cards, data]);
      setToast({ open: true, message: 'Card issued', severity: 'success' });
    } catch {
      setToast({ open: true, message: 'Issue failed', severity: 'error' });
    }
    setIssueOpen(false);
  };

  // Approve/Reject handlers
  const handleAppAction = async (id, status) => {
    const token = localStorage.getItem('token');
    setAppLoading(true);
    try {
      await axios.patch(`https://api.safenetbanking.com/api/admin/card_applications/${id}`, { status }, {
        headers: { Authorization: 'Bearer ' + token }
      });
      setApplications(applications.map(a => a.id === id ? { ...a, status } : a));
      setToast({ open: true, message: `Application ${status}`, severity: 'success' });
    } catch {
      setToast({ open: true, message: 'Action failed', severity: 'error' });
    }
    setAppLoading(false);
  };

  return (
    <Box>
      <Typography variant="h4" mb={2}>All Cards</Typography>
      <Button variant="contained" onClick={handleIssue} sx={{ mb: 2 }}>ISSUE NEW CARD</Button>
      <TableContainer component={Paper} sx={{ mb: 4 }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>ID</TableCell>
              <TableCell>User ID</TableCell>
              <TableCell>Card Number</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Expiry</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {cards.filter(c =>
              (c.card_number?.toLowerCase().includes(search.toLowerCase()) ||
               c.type?.toLowerCase().includes(search.toLowerCase()) ||
               c.status?.toLowerCase().includes(search.toLowerCase()) ||
               c.expiry?.toLowerCase().includes(search.toLowerCase()) ||
               c.user_id?.toString().includes(search) ||
               c.id?.toString().includes(search)
              )
            ).map(card => (
              <TableRow key={card.id}>
                <TableCell>{card.id}</TableCell>
                <TableCell>{card.user_id}</TableCell>
                <TableCell>{card.card_number}</TableCell>
                <TableCell>{card.type}</TableCell>
                <TableCell>{card.status}</TableCell>
                <TableCell>{card.expiry}</TableCell>
                <TableCell>
                  <Button size="small" variant="outlined" onClick={() => handleEdit(card)} sx={{ mr: 1 }}>Edit</Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Typography variant="h5" mb={2}>Card Applications</Typography>
      <TableContainer component={Paper} sx={{ mb: 4 }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>User</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Applied</TableCell>
              <TableCell>Reviewed</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {applications.map(app => (
              <TableRow key={app.id}>
                <TableCell>{app.full_name}</TableCell>
                <TableCell>{app.email}</TableCell>
                <TableCell>{app.type}</TableCell>
                <TableCell>{app.status}</TableCell>
                <TableCell>{new Date(app.created_at).toLocaleString()}</TableCell>
                <TableCell>{app.reviewed_at ? new Date(app.reviewed_at).toLocaleString() : '-'}</TableCell>
                <TableCell>
                  {app.status === 'pending' && (
                    <>
                      <Button size="small" color="success" onClick={() => handleAppAction(app.id, 'approved')}>Approve</Button>
                      <Button size="small" color="error" onClick={() => handleAppAction(app.id, 'rejected')}>Reject</Button>
                    </>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <AdminCardEditDialog open={editOpen} card={editCard} onClose={handleEditClose} onSave={handleEditSave} />
      <AdminCardIssueDialog open={issueOpen} onClose={handleIssueClose} onIssue={handleIssueSave} />
      <Toast open={toast.open} message={toast.message} severity={toast.severity} onClose={() => setToast({ ...toast, open: false })} />
    </Box>
  );
}
